import { DiffViewerContentCols, DiffViewerTitleCols, Row } from '@/components';
import {
  useAccountTitleOptions,
  useAccountingTypeOptions,
} from '@/features/master-table/hooks';
import { useLocaledAmount } from '@/providers';
import type { DiffViewConfigType } from '@/types/diff-view-config';
import {
  AmountUnitLabelModifier,
  FALLBACK_TEXT,
  formatValueByUnit,
} from '@/utils';
import { type FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { InspectionEvaluationRsModel } from '../../../_models/project';

const ReflectionStatusBasedOnOpinionModel =
  InspectionEvaluationRsModel.shape.reflectionStatusBasedOnOpinion;

type ReflectionStatusBasedOnOpinion = NonNullable<
  NonNullable<
    z.infer<typeof ReflectionStatusBasedOnOpinionModel>
  >['specialAccount']
>[number];

type Props = {
  sourceReflectedAmountOfSpecialAccounts: ReflectionStatusBasedOnOpinion[]; // left side
  targetReflectedAmountOfSpecialAccounts: ReflectionStatusBasedOnOpinion[]; // right side
};

type DiffViewConfigItem = DiffViewConfigType<ReflectionStatusBasedOnOpinion>;

export const ReflectedAmountOfSpecialAccountDiffView: FC<Props> = ({
  sourceReflectedAmountOfSpecialAccounts,
  targetReflectedAmountOfSpecialAccounts,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const accountingTypeOptions = useAccountingTypeOptions();
  const accountTitleOptions = useAccountTitleOptions();

  const diffViewConfig: DiffViewConfigItem[] = [
    {
      prop: 'accountingTypeId',
      label: t('accounting-name'),
      converter: (value: ReflectionStatusBasedOnOpinion['accountingTypeId']) =>
        accountingTypeOptions.data?.find((option) => option.value === value)
          ?.label ?? FALLBACK_TEXT,
    },
    {
      prop: 'accountTitleId',
      label: t('account-title'),
      converter: (value: ReflectionStatusBasedOnOpinion['accountTitleId']) =>
        accountTitleOptions.data?.find((option) => option.value === value)
          ?.label ?? FALLBACK_TEXT,
    },
    {
      prop: 'reflectedAmount',
      label: t('reflected-amount-with-unit', {
        unit: AmountUnitLabelModifier[unit],
      }),
      converter: (value: ReflectionStatusBasedOnOpinion['reflectedAmount']) =>
        typeof value === 'number'
          ? formatValueByUnit(value, { operation: 'round', unit })
          : FALLBACK_TEXT,
      isMonetaryField: true,
    },
  ];

  const ReflectedAmountOfSpecialAccountLength = Math.max(
    sourceReflectedAmountOfSpecialAccounts.length,
    targetReflectedAmountOfSpecialAccounts.length,
  );

  return (
    <Row gutter={[8, 0]} style={{ marginLeft: 12, width: '100%' }}>
      {[...Array(ReflectedAmountOfSpecialAccountLength)].map((_, index) => {
        const sourceReflectedAmountOfSpecialAccount =
          sourceReflectedAmountOfSpecialAccounts[index];
        const targetReflectedAmountOfSpecialAccount =
          targetReflectedAmountOfSpecialAccounts[index];
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
          <Fragment key={`reflected-amount-of-special-account-${index}`}>
            <DiffViewerTitleCols
              index={index + 1}
              label={t('reflected-amount-of-special-account')}
            />
            {diffViewConfig.map(
              ({ prop, label, converter, isMonetaryField }) => (
                <DiffViewerContentCols<ReflectionStatusBasedOnOpinion>
                  key={`reflected-amount-of-special-account-${index}-${prop}`}
                  legend={label}
                  prop={prop}
                  textConverter={converter}
                  isMonetaryField={isMonetaryField}
                  showLegendAnytime={{ source: true, target: true }}
                  source={sourceReflectedAmountOfSpecialAccount}
                  target={targetReflectedAmountOfSpecialAccount}
                />
              ),
            )}
          </Fragment>
        );
      })}
    </Row>
  );
};
